import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { DateTime } from 'luxon';
import { lastValueFrom, Observable } from 'rxjs';
import { DateRange } from 'src/app/common/daterange';
import { LazyUserSelection, LazyUserSelectionSerializable } from 'src/app/components/user-filter-lazy';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { appendToolStepFromStatus } from 'src/app/services/copilot/copilot-tool-steps.util';
import {
  AgentGatewayResponseBody, AgentGatewayStatusEvent, CoachingSuggestion, RecommendationCard, ReportLink, TokenQuotaUsage
} from 'src/app/services/copilot/copilot.types';
import { parseQuotaBucket, parseTopUsersToday } from 'src/app/services/copilot/token-quota.util';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ResolveService } from 'src/app/services/resolve.service';
import { RefreshToken } from 'src/app/store/auth.actions';
import { AuthState } from 'src/app/store/auth/auth.state';
import { ManagerState } from 'src/app/store/manager/manager.state';
import { canViewUserScreencasts } from 'src/app/util/screencast-helpers';
import {
  AgentChatConversationDetail, AgentChatConversationsResponse, AlertSignalGroup, AlertType, AlertTypeResponse, AskAiFeedbackReason,
  ConversationTurnStep, GuidanceAnswer, Period, PersonCardDailyColumn, Sensitivity, SensitivityThreshold, Severity, Signal,
  SignalsResponse, SignalThreshold, StoredPersonWeek, TokenUsage, UserGroupedSignalsResponse, UserSignalGroup
} from '../models';
import {
  buildSignalsFromSignalsResponse, GUIDANCE, GUIDANCE_FALLBACK, mapAlertTypeResponses, NOTCH_MIN,
  parseAlertTypeResponses,
  parseSignalsResponse, parseStoredPersonWeeks, parseUserGroupedSignalsResponse, PERIODS,
  SEV_ORDER
} from './ask-ai-mock-data';
import { buildFiredSignalsForUser, buildPersonCardDailyChart, mondayOfWeekContaining, SignalBandConfig } from './ask-ai-person-signals.util';
import { joinWithAnd } from './ask-ai-signal-body';
import {
  bodyToTextParts, buildPersonCard, buildPersonCardAskPrompt, buildPersonCardSignalLink, lineToTextParts, PERSON_CARD_SIGNAL_KEY,
  PersonCard, personSignalTypeLabel
} from './person-signal-card';
import { buildPersonWeekEvidence, PersonWeekEvidence } from './person-week-evidence';
import { personWeekSignals } from './person-week-preview';

// A question is guidance, not analysis, when it asks how something works or how to handle it.
// "How is idle time calculated" and "how should I bring this up" both land here; "how much idle
// time did Sales have" doesn't, because it names a subject whose data has to be read.
const GUIDANCE_OPENERS = [
  'how do i', 'how should i', 'how can i', 'how would i', 'how does', 'how do you', 'how is',
  'how are', 'what is', 'what are', 'what does', 'what counts as', 'why does', 'explain',
  'best way to', 'tips for', 'advice on', 'talk to', 'bring up',
];

// Sanity bound on getConversation's page-walking loop — a runaway/incorrect `total` in the
// response must not turn into an unbounded number of requests. 50 pages at the sample response's
// 20-per-page default is 1000 messages, generous for anything a real conversation would reach.
const GET_CONVERSATION_MAX_PAGES = 50;

interface FetchedSignals {
  groups: AlertSignalGroup[];
  personWeeks: StoredPersonWeek[];
  hasPersonWeeks: boolean;
  total?: number;
}

@Injectable({ providedIn: 'root' })
export class AskAiService {
  constructor(
    private store: Store, private api: ApiService, private resolve: ResolveService,
    private authService: AuthService, private growthBook: GrowthBookService,
  ) { }

  get liveAnalysisEnabled(): boolean {
    return this.growthBook.getFeatureValue<boolean>('ask-ai-new-signals-page', false) === true;
  }

  /**
   * AA-214 combined person cards vs the original by-alert-type Signal cards — same growthbook-
   * gated rollout pattern as liveAnalysisEnabled above. Off (by-alert-type) by default until this
   * is explicitly turned on for a company. The two views are mutually exclusive per load — see
   * AskAiState.loadSignals/loadDismissedSignals, which fetch only whichever this says is active.
   */
  get personCardsEnabled(): boolean {
    return this.growthBook.getFeatureValue<boolean>('ask-ai-group-by-user-signals', false) === true;
  }

  /**
   * Streams a real Agent Gateway answer for one turn via the core API — POST (SSE)
   * activity/agent-gateway/agent/chat/stream, auth'd/hosted the same way every other call in
   * this file is (this.api.getFullApiUrl), not the separate integrations host
   * CopilotFacadeService/AgentGatewayService use for the Insights side panel. Reported back
   * through the two callbacks below instead of a message list, so AskAiState can patch its own
   * ConversationTurn. Used for a signal's own askPrompt (OpenConversation/RetryConversationTurn)
   * and for a typed follow-up/ad-hoc question (SendQuestion) alike — `message` is whichever text
   * applies. `gatewayConversationId`, when passed, resends the id an earlier turn in the same
   * conversation got back, so the gateway keeps multi-turn memory across follow-ups.
   * Never throws: every failure (network, non-2xx, malformed SSE) maps to the error branch.
   */
  async streamLiveAnswer(
    message: string,
    opts: {
      dateRange: { from?: string, to?: string }; filters?: Record<string, unknown>; gatewayConversationId?: string;
      timezone?: string;
    },
    handlers: { onSteps: (steps: ConversationTurnStep[]) => void, onToken: (answerSoFar: string) => void },
  ): Promise<
    | {
      answer: string; usage: TokenUsage | null; conversationId?: string; requestId?: string;
      reportLinks?: ReportLink[]; recommendationCard?: RecommendationCard; coachingSuggestions?: CoachingSuggestion[];
    }
    | { error: string, retryable: boolean }
  > {
    const auth = this.store.selectSnapshot(AuthState);
    // Prefer the ask-ai-timezone-picker's pick; fall back to the account default.
    const timezone = opts.timezone || this.store.selectSnapshot(AuthState.userTimezone);
    const context = {
      route: 'ask-ai',
      companyId: auth?.company?.id,
      userId: auth?.user?.id,
      role: auth?.company?.role,
      timezone,
      dateRange: opts.dateRange.from && opts.dateRange.to ?
        { from: this.clampToToday(opts.dateRange.from, timezone), to: this.clampToToday(opts.dateRange.to, timezone) } : undefined,
      filters: opts.filters,
    };
    const body = { message, context, conversationId: opts.gatewayConversationId };

    let response: Response;
    try {
      response = await this.authorizedFetch('activity/agent-gateway/agent/chat/stream', body, { accept: 'text/event-stream' });
    } catch {
      return { error: `Couldn't reach the AI Agent — try again.`, retryable: true };
    }
    if (!response.ok || !response.body) {
      return { error: `Couldn't reach the AI Agent — try again.`, retryable: true };
    }

    let steps: ConversationTurnStep[] | undefined;
    return this.consumeAgentChatStream(response.body, {
      onToken: handlers.onToken,
      onStatusEvent: evt => {
        const next = appendToolStepFromStatus(steps, evt);
        if (next !== steps) {
          steps = next;
          handlers.onSteps(steps ?? []);
        }
      },
    });
  }

  // Caps a date at the end of "today" in the given timezone — the gateway's MCP tools reject
  // future dates outright (AA-202), so a "current week/month" range needs its future end clamped.
  private clampToToday(isoDate: string, timezone?: string): string {
    const endOfToday = (timezone ? DateTime.utc().setZone(timezone) : DateTime.utc()).endOf('day').toUTC().toISO();
    return isoDate > endOfToday ? endOfToday : isoDate;
  }

  /** Submits helpful/not-helpful feedback for a live turn — POST
   * activity/agent-gateway/agent/feedback, same this.api.request(...) pattern as every other
   * call in this file (unlike the streaming call above, a plain JSON POST doesn't need the raw
   * fetch below). A no-op for a fixture turn (no real gatewayConversationId/requestId to
   * correlate it against) and best-effort otherwise — a failed feedback POST isn't worth
   * surfacing anywhere.
   *
   * `requestId` is the turn's identity as far as the gateway is concerned: it stores one row per
   * (conversation, requestId), so re-voting or picking a reason after the thumb updates that one
   * row, while a retried turn (new requestId, same local turnId) records separately. `messageId`
   * rides along for correlation only — it is rebuilt from array position on reload and so can't
   * be keyed on. */
  async submitTurnFeedback(
    gatewayConversationId: string | undefined, requestId: string | undefined, turnId: string,
    rating: 'helpful' | 'not_helpful', reason?: AskAiFeedbackReason,
  ): Promise<void> {
    if (!gatewayConversationId || !requestId) return;
    try {
      await lastValueFrom(this.api.request(
        'post', 'activity/agent-gateway/agent/feedback',
        {
          rating, conversationId: gatewayConversationId, messageId: turnId, requestId,
          ...(reason ? { reason } : {}),
        }, {}, true, '2.0',
      ));
    } catch {
      // best-effort, see doc comment above
    }
  }

  /** Soft daily/monthly token quota for the signed-in user (Ask AI's Usage menu) — GET
   * activity/agent-gateway/agent/usage via the core API, same this.api.request(...) pattern as
   * every other call in this file, not the separate integrations host AgentGatewayService uses
   * for the Copilot side panel. Never throws — returns null on any transport/parse failure. */
  async getTokenQuotaUsage(pricingPlan?: string): Promise<TokenQuotaUsage | null> {
    const plan = pricingPlan?.trim();
    try {
      const body = await lastValueFrom(this.api.request<Record<string, unknown>>(
        'get', 'activity/agent-gateway/agent/usage', plan ? { pricingPlan: plan } : {}, {}, true, '2.0',
      ));
      if (!body?.daily || !body?.monthly) return null;
      const daily = parseQuotaBucket(body.daily);
      const monthly = parseQuotaBucket(body.monthly);
      if (!daily || !monthly) return null;
      return { daily, monthly, topUsersToday: parseTopUsersToday(body.topUsersToday) };
    } catch {
      return null;
    }
  }

  /**
   * Chat history list for the signed-in caller — GET activity/agent-gateway/agent/chat/conversations,
   * same this.api.request(...) pattern as every other call in this file. `limit`/`offset` page
   * through it. Returns the full envelope (not just `data`) since `total` drives the History nav
   * item's badge (AskAiState.loadConversationHistory), not just the list itself. Never throws —
   * logs to Sentry and returns undefined on failure, same as getSensitivityCounts.
   */
  async listConversations(limit?: number, offset?: number): Promise<AgentChatConversationsResponse | undefined> {
    const params: Record<string, number> = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    try {
      return await lastValueFrom(this.api.request<AgentChatConversationsResponse>(
        'get', 'activity/agent-gateway/agent/chat/conversations', params, {}, true, '2.0',
      ));
    } catch (error) {
      Sentry.captureException(error, { level: 'warning', extra: { call: 'activity/agent-gateway/agent/chat/conversations', limit, offset } });
      return undefined;
    }
  }

  /**
   * AA-353: the canonical conversationId for a signal (and, for a person-week card, its weekStart),
   * or undefined when none exists yet. GET activity/agent-gateway/agent/chat/conversations with a
   * `signalId` (+ optional `weekStart`) filter — the backend (AA-352, api-server PR #4466) persists
   * signalId/weekStart on conversations and resolves one canonical conversation per signal/week, so
   * Analyze/suggested-question can reopen the existing thread across refresh/devices instead of
   * minting a duplicate. Returns the first match's conversationId.
   *
   * Never throws — logs to Sentry and returns undefined on any failure so the caller falls back to
   * creating a new conversation rather than hard-erroring (graceful degradation until #4466 ships).
   */
  async findConversationForSignal(signalId: string, weekStart?: string): Promise<string | undefined> {
    if (!signalId) return undefined;
    const params: Record<string, string> = { signalId };
    if (weekStart) params.weekStart = weekStart;
    try {
      const response = await lastValueFrom(this.api.request<AgentChatConversationsResponse>(
        'get', 'activity/agent-gateway/agent/chat/conversations', params, {}, true, '2.0',
      ));
      return response?.data?.[0]?.conversationId;
    } catch (error) {
      Sentry.captureException(
        error, { level: 'warning', extra: { call: 'activity/agent-gateway/agent/chat/conversations', signalId, weekStart } },
      );
      return undefined;
    }
  }

  /**
   * One conversation's full history — GET activity/agent-gateway/agent/chat/conversations/{conversationId}.
   * `messages` is itself paginated (its own `page`/`limit`/`total`, distinct from listConversations'
   * pagination of conversations) — page 1 alone silently truncates any conversation with more
   * messages than one page holds, so this walks every page and concatenates before returning.
   * Capped at GET_CONVERSATION_MAX_PAGES as a sanity bound against a runaway `total`. Never throws —
   * logs to Sentry and returns undefined if even the first page fails; a later page failing instead
   * just stops there and returns what was fetched so far, rather than losing the whole conversation.
   */
  async getConversation(conversationId: string): Promise<AgentChatConversationDetail | undefined> {
    let first: AgentChatConversationDetail | undefined;
    try {
      first = await this.fetchConversationPage(conversationId, 1);
    } catch (error) {
      Sentry.captureException(
        error, { level: 'warning', extra: { call: 'activity/agent-gateway/agent/chat/conversations/:id', conversationId, page: 1 } },
      );
      return undefined;
    }
    if (!first) return undefined;

    let messages = first.messages ?? [];
    const limit = first.limit || messages.length || 1;
    const totalPages = Math.min(Math.ceil((first.total || messages.length) / limit) || 1, GET_CONVERSATION_MAX_PAGES);
    for (let page = 2; page <= totalPages && messages.length < first.total; page++) {
      try {
        const next = await this.fetchConversationPage(conversationId, page, limit);
        if (!next?.messages?.length) break;
        messages = messages.concat(next.messages);
      } catch (error) {
        Sentry.captureException(
          error, { level: 'warning', extra: { call: 'activity/agent-gateway/agent/chat/conversations/:id', conversationId, page } },
        );
        break;
      }
    }
    return { ...first, messages };
  }

  private fetchConversationPage(
    conversationId: string, page: number, limit?: number,
  ): Promise<AgentChatConversationDetail | undefined> {
    const params: Record<string, number> = { page };
    if (limit !== undefined) params.limit = limit;
    return lastValueFrom(this.api.request<AgentChatConversationDetail>(
      'get', `activity/agent-gateway/agent/chat/conversations/${encodeURIComponent(conversationId)}`, params, {}, true, '2.0',
    ));
  }

  // Parses the chat/stream SSE body down to what Ask AI's turn model needs (answer, usage,
  // conversationId, requestId, tool steps via onStatusEvent, plus reportLinks/recommendationCard/
  // coachingSuggestions) — a scoped-down counterpart to AgentGatewayService.consumeSse, which also
  // parses charts/tables/decision/persona/etc. that only the Copilot side panel renders
  // (STAFF-145718 follow-up — charts/tables aren't rendered here yet, see
  // ConversationTurn.reportLinks).
  private async consumeAgentChatStream(
    stream: ReadableStream<Uint8Array>,
    handlers: { onToken: (answerSoFar: string) => void, onStatusEvent: (evt: AgentGatewayStatusEvent) => void },
  ): Promise<
    | {
      answer: string; usage: TokenUsage | null; conversationId?: string; requestId?: string;
      reportLinks?: ReportLink[]; recommendationCard?: RecommendationCard; coachingSuggestions?: CoachingSuggestion[];
    }
    | { error: string, retryable: boolean }
  > {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let answerSoFar = '';
    let result:
      | {
        answer: string; usage: TokenUsage | null; conversationId?: string; requestId?: string;
        reportLinks?: ReportLink[]; recommendationCard?: RecommendationCard; coachingSuggestions?: CoachingSuggestion[];
      }
      | { error: string, retryable: boolean }
      | null = null;

    const handleEvent = (name: string, data: string) => {
      if (name === 'token') {
        const chunk = this.safeParseJson<{ text?: string }>(data)?.text ?? '';
        if (chunk) {
          answerSoFar += chunk;
          handlers.onToken(answerSoFar);
        }
      } else if (name === 'status') {
        const evt = this.safeParseJson<AgentGatewayStatusEvent>(data);
        if (evt && typeof evt.state === 'string') handlers.onStatusEvent(evt);
      } else if (name === 'final') {
        const parsed = this.safeParseJson<AgentGatewayResponseBody>(data);
        result = parsed && typeof parsed.answer === 'string'
          ? {
            answer: parsed.answer,
            usage: this.parseTokenUsage(parsed.meta?.usage),
            conversationId: typeof parsed.conversationId === 'string' ? parsed.conversationId : undefined,
            requestId: (typeof parsed.meta?.requestId === 'string' && parsed.meta.requestId)
              || (typeof parsed.debug?.requestId === 'string' && parsed.debug.requestId) || undefined,
            reportLinks: this.parseReportLinks(parsed.reportLinks),
            recommendationCard: this.parseRecommendationCard(parsed.recommendationCard) ?? undefined,
            coachingSuggestions: this.parseCoachingSuggestions(parsed.coachingSuggestions),
          }
          : { error: `Couldn't reach the AI Agent — try again.`, retryable: true };
      } else if (name === 'error') {
        // Gateway signalled a failure mid-stream (emits `error` then `done`, no `final`).
        const err = this.safeParseJson<{ message?: unknown, retryable?: unknown }>(data);
        result = {
          error: (typeof err?.message === 'string' && err.message.trim()) || `Couldn't reach the AI Agent — try again.`,
          retryable: typeof err?.retryable === 'boolean' ? err.retryable : true,
        };
      }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        this.parseSseFrame(frame, handleEvent);
      }
    }
    if (buffer.trim().length > 0) this.parseSseFrame(buffer, handleEvent);

    return result ?? { error: `Couldn't reach the AI Agent — try again.`, retryable: true };
  }

  // Parses one SSE frame's `event:`/`data:` lines — same framing AgentGatewayService.parseFrame
  // reads from the same backend, just duplicated here rather than shared: that method is private
  // to a class this file no longer depends on.
  private parseSseFrame(frame: string, onEvent: (name: string, data: string) => void): void {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }
    if (dataLines.length > 0) onEvent(event, dataLines.join('\n'));
  }

  private safeParseJson<T>(raw: string): T | null {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  private parseTokenUsage(raw: unknown): TokenUsage | null {
    if (!raw || typeof raw !== 'object') return null;
    const u = raw as { promptTokens?: unknown, completionTokens?: unknown, totalTokens?: unknown };
    const promptTokens = Number(u.promptTokens);
    const completionTokens = Number(u.completionTokens);
    const totalTokens = Number(u.totalTokens);
    if (![promptTokens, completionTokens, totalTokens].every(n => Number.isFinite(n))) return null;
    return { promptTokens, completionTokens, totalTokens };
  }

  // Keep only well-formed { id, label, route, phrases } report links — same validation
  // AgentGatewayService.parseReportLinks applies for the Copilot side panel, duplicated here
  // rather than shared. `route` must be internal (starts with '/') — never an external URL.
  private parseReportLinks(raw: unknown): ReportLink[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((a): a is ReportLink => {
        const row = a as ReportLink;
        return !!row && typeof row.id === 'string' && typeof row.label === 'string'
          && typeof row.route === 'string' && row.route.startsWith('/') && Array.isArray(row.phrases);
      })
      .map(a => ({
        id: a.id, label: a.label, route: a.route,
        phrases: a.phrases.filter((p): p is string => typeof p === 'string' && !!p.trim()),
        params: a.params && typeof a.params === 'object' ? a.params : undefined,
        icon: typeof a.icon === 'string' ? a.icon : undefined,
      }));
  }

  // Keep only a well-formed recommendation card — a scoped-down duplicate of
  // AgentGatewayService.parseRecommendationCard (drops the `actions`/`draftLabel` validation, since
  // the Ask AI draft panel only ever shows/copies the message text, unlike the side panel's Send/
  // Skip/Ask-for-a-call buttons — see AnalyzeConversationComponent.draftSource).
  private parseRecommendationCard(raw: unknown): RecommendationCard | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    const title = typeof o.title === 'string' ? o.title.trim() : '';
    const draftMessage = typeof o.draftMessage === 'string' ? o.draftMessage.trim() : '';
    if (!title || !draftMessage) return null;
    return {
      title, draftMessage, actions: [],
      ...(typeof o.body === 'string' && o.body.trim() ? { body: o.body.trim() } : {}),
    };
  }

  // Keep only well-formed coaching suggestions from the gateway response — a scoped-down duplicate
  // of AgentGatewayService.parseCoachingSuggestions (same field validation).
  private parseCoachingSuggestions(raw: unknown): CoachingSuggestion[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(
        (c): c is CoachingSuggestion =>
          !!c && typeof (c as CoachingSuggestion).approach === 'string'
          && typeof (c as CoachingSuggestion).draftMessage === 'string',
      )
      .map(c => ({
        issue: typeof c.issue === 'string' ? c.issue : 'workload_check_in',
        approach: c.approach.trim(),
        draftMessage: c.draftMessage.trim(),
        followUpActions: Array.isArray(c.followUpActions)
          ? c.followUpActions.filter((a): a is string => typeof a === 'string' && a.trim().length > 0)
          : [],
      }));
  }

  // Raw fetch (not this.api.request/HttpClient — it can't stream an SSE response) against the
  // same core API host+version every other call in this file uses, replicating just the pieces
  // of HttpInterceptorService's auth handling a plain HttpClient call gets for free: the
  // company query param, the JWT/Bearer Authorization header for whichever auth mode this
  // company is on, and a refresh-and-retry-once on a 401 (raw fetch bypasses the interceptor
  // entirely, so nothing else provides that retry here).
  private async authorizedFetch(path: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<Response> {
    const url = new URL(`${this.api.getFullApiUrl('2.0')}/${path}`);
    const company = this.store.selectSnapshot(AuthState.company);
    if (company?.id) url.searchParams.set('company', company.id);

    const doFetch = () => {
      const auth = this.store.selectSnapshot(AuthState);
      const cookieAuth = !!auth?.cookieAuth || this.authService.version === 'v2';
      const headers: Record<string, string> = { 'Content-Type': 'application/json', ...extraHeaders };
      if (auth?.token) Object.assign(headers, this.authService.getTokenHeader(auth.token));
      return fetch(url.toString(), {
        method: 'POST', headers, body: JSON.stringify(body),
        credentials: cookieAuth ? 'include' : 'same-origin',
      });
    };

    const response = await doFetch();
    if (response.status !== 401) return response;
    await lastValueFrom(this.store.dispatch(new RefreshToken()));
    return doFetch();
  }

  // `from`/`to` (ISO datetime, from the picked DateRange's asApiParams()) drive the live fetch;
  // `label` is that same range's display text, attached to each generated Signal as `period`.
  // Backend threshold engine is live now (AA-138/AA-140) — this used to fall back to the local
  // SIGNALS fixture when no range was known yet or the live fetch failed; that fixture masked real
  // outages as "no signals configured yet", so callers now see the failure (via a thrown error)
  // and decide how to show that instead. `fallbackPeriodKey` is unused now that the fixture
  // fallback is gone but is left in the signature — every call site still threads a real value
  // through it for other purposes upstream.
  async getSignals(
    from: string | undefined, to: string | undefined, label: string, fallbackPeriodKey?: string,
    thresholds?: Record<string, SignalThreshold>, userFilter?: LazyUserSelectionSerializable,
  ): Promise<{ signals: Signal[], personWeeks: StoredPersonWeek[] }> {
    if (!from || !to) return { signals: [], personWeeks: [] };
    const fetched = await this.fetchSignalsResponse(from, to);
    if (!fetched) throw new Error('Failed to fetch activity/agent-gateway/signals');
    const groups = this.scopeGroupsToSelection(fetched.groups, userFilter);
    const { names, screencastsAllowed } = await this.resolveFlaggedUsers(groups);
    const signals = buildSignalsFromSignalsResponse(groups, label, names, screencastsAllowed, thresholds);
    // Default GET omits dismissed weeks; drop any that still arrive with dismissed: true.
    const activeWeeks = fetched.personWeeks.filter(week => !week.dismissed);
    return { signals, personWeeks: this.scopePersonWeeksToSelection(activeWeeks, userFilter) };
  }

  // Person-week cards carry their own userId, so scope them to the picked selection the same way
  // scopeGroupsToSelection narrows the grouped signals — an explicit user pick filters by id list,
  // a group pick resolves against the managed users, and "everyone" passes through untouched.
  private scopePersonWeeksToSelection(
    weeks: StoredPersonWeek[], userFilter?: LazyUserSelectionSerializable,
  ): StoredPersonWeek[] {
    // fromSerializable treats a missing filter as "everyone" (LazyUserSelection.default).
    const selection = LazyUserSelection.fromSerializable(userFilter ?? {});
    const selectsEveryone = selection.type === 'users' ? selection.users.all : selection.groups.all;
    if (selectsEveryone) return weeks;
    let allowed: Set<string>;
    if (selection.type === 'users') {
      allowed = new Set(selection.users.list);
    } else {
      const managed = this.store.selectSnapshot(ManagerState.users.onReports.includeArchived(selection.includeArchivedUsers));
      if (!managed?.length) return weeks;
      allowed = new Set(managed.filter(selection.isUserSelected).map(user => user.id));
    }
    return weeks.filter(week => allowed.has(week.userId));
  }

  /**
   * History / Dismissed's data source — GET activity/agent-gateway/signals with
   * includeDismissed=true. When the response carries `personWeeks` (AA-247), dismissed rows are
   * the weeks marked `dismissed: true` (mapped to Signal via personWeekSignals). Otherwise falls
   * back to by-alert-type groups filtered on AlertSeverityEntry.status. Never throws — same
   * contract as listConversations.
   */
  async getDismissedSignals(
    from: string | undefined, to: string | undefined, label: string,
    thresholds?: Record<string, SignalThreshold>, userFilter?: LazyUserSelectionSerializable,
  ): Promise<{ signals: Signal[], personWeekSource: boolean } | undefined> {
    if (!from || !to) return { signals: [], personWeekSource: false };
    const fetched = await this.fetchSignalsResponse(from, to, true);
    if (!fetched) return undefined;
    if (fetched.hasPersonWeeks) {
      const weeks = this.scopePersonWeeksToSelection(
        fetched.personWeeks.filter(week => week.dismissed === true), userFilter,
      );
      return { signals: personWeekSignals(weeks, [], this.store.selectSnapshot(AuthState.firstDayOfWeek)), personWeekSource: true };
    }
    const dismissedOnly = this.filterGroupsByStatus(fetched.groups, 'dismissed');
    const groups = this.scopeGroupsToSelection(dismissedOnly, userFilter);
    const { names, screencastsAllowed } = await this.resolveFlaggedUsers(groups);
    return {
      signals: buildSignalsFromSignalsResponse(groups, label, names, screencastsAllowed, thresholds),
      personWeekSource: false,
    };
  }

  /**
   * One page of History / Dismissed's person-week feed, newest first and independent of the date
   * range selected in Needs you (AA-327). The API pages active and dismissed cards together, so a
   * page may hold no dismissed rows: `scanned` (cards the page covered) is what advances the
   * offset, not `signals.length`. Never throws — undefined on a failed fetch.
   */
  async getDismissedPersonWeekPage(
    offset: number, limit: number, userFilter?: LazyUserSelectionSerializable,
  ): Promise<{ signals: Signal[], scanned: number, total: number, personWeekSource: boolean } | undefined> {
    const fetched = await this.fetchSignalsResponse(undefined, undefined, true, { limit, offset });
    if (!fetched) return undefined;
    const weeks = this.scopePersonWeeksToSelection(
      fetched.personWeeks.filter(week => week.dismissed === true), userFilter,
    );
    return {
      signals: personWeekSignals(weeks, [], this.store.selectSnapshot(AuthState.firstDayOfWeek)),
      scanned: fetched.personWeeks.length,
      total: fetched.total ?? 0,
      personWeekSource: fetched.hasPersonWeeks,
    };
  }

  // Keeps only each group's entries whose status.status matches — an entry with no status at all
  // (never touched) never matches either side, same as one explicitly marked 'active'. A bucket
  // that empties out is dropped entirely, same "no card for an empty bucket" rule
  // scopeGroupsToSelection already applies below.
  private filterGroupsByStatus(groups: AlertSignalGroup[], status: 'active' | 'dismissed'): AlertSignalGroup[] {
    return groups.reduce<AlertSignalGroup[]>((kept, group) => {
      const bySeverity: AlertSignalGroup['bySeverity'] = {};
      (Object.keys(group.bySeverity) as Severity[]).forEach(severity => {
        const entries = group.bySeverity[severity]?.filter(entry => entry.status?.status === status);
        if (entries?.length) bySeverity[severity] = entries;
      });
      if (Object.keys(bySeverity).length) kept.push({ alertId: group.alertId, bySeverity });
      return kept;
    }, []);
  }

  /**
   * Marks one or more raw signals dismissed/active for the caller — POST
   * activity/agent-gateway/signals/status, same this.api.request(...) pattern as every other call
   * in this file. Pass every id a grouped card represents (Signal.signalIds), not the card's own
   * synthetic `id` (`${alertId}-${severity}`) — the backend has never heard of that one. Called
   * fire-and-forget from AskAiState.dismissSignal/undoDismissSignal and from the person-week
   * dismiss/undo actions (week id plus each type id). Those already apply the local,
   * instant hide/show regardless of this call's outcome — this just makes the change survive a
   * reload/new session and keeps a future GET activity/agent-gateway/signals (which omits
   * dismissed entries by default) correct. Never throws — logs to Sentry and resolves false on
   * failure.
   */
  async setSignalStatus(signalIds: string[], status: 'active' | 'dismissed'): Promise<boolean> {
    if (!signalIds.length) return true;
    try {
      await lastValueFrom(this.api.request(
        'post', 'activity/agent-gateway/signals/status', { signalIds, status }, {}, true, '2.0',
      ));
      return true;
    } catch (error) {
      Sentry.captureException(
        error, { level: 'warning', extra: { call: 'activity/agent-gateway/signals/status', status, count: signalIds.length } },
      );
      return false;
    }
  }

  // Each group's flagged-user entries carry a userId, not a display name — resolves every flagged
  // id across all groups (every severity bucket) to its User via the app-wide id->user cache
  // (ResolveService, same one <app-user> uses elsewhere) in one batched call, rather than one
  // lookup per signal. `detail: 'settings'` also pulls the screenshots/videos fields
  // canViewUserScreencasts needs to gate each row's Screencasts link (STAFF-145718 follow-up: the
  // link must respect the company setting and the flagged user's own capture settings, not just be
  // always shown). Falls back to the raw id for any user the lookup can't resolve (e.g. deleted)
  // instead of dropping that signal; screencasts default to not allowed when unresolved.
  private async resolveFlaggedUsers(
    groups: AlertSignalGroup[],
  ): Promise<{ names: Map<string, string>, screencastsAllowed: Map<string, boolean> }> {
    const ids = Array.from(new Set(
      groups.flatMap(g => Object.values(g.bySeverity).flatMap(entries => (entries ?? []).map(e => e.id))),
    ));
    const names = new Map<string, string>();
    const screencastsAllowed = new Map<string, boolean>();
    if (!ids.length) return { names, screencastsAllowed };
    const users = await this.resolve.resolveUsers(ids, { detail: 'settings' });
    const company = this.store.selectSnapshot(AuthState.company);
    const authUserId = this.store.selectSnapshot(AuthState.user)?.id ?? '';
    ids.forEach(id => {
      const user = users.get(id);
      names.set(id, user?.name ?? id);
      // canViewUserScreencasts treats a missing user as "can't verify" (returns false) despite its
      // non-optional param type - same as the ai-adoption sidepanel's screencastsEnabledForUser$.
      screencastsAllowed.set(id, company && user ? canViewUserScreencasts(company, authUserId, user) : false);

    });
    return { names, screencastsAllowed };
  }

  // GET activity/agent-gateway/signals (was activity/agent-gateway/alerts/customer) — same
  // from/to params, but a { signals: [...] } envelope now rather than a bare array, unlike
  // fetchAlertTypeResponses below. Never throws itself — a failed/malformed response is logged to
  // Sentry and reported as undefined; getSignals turns that into a thrown error for its caller.
  /**
   * How many users each alert type would flag at each sensitivity notch, for **last week across
   * every user in the account** — the number the Settings screen's "Last week that would have
   * flagged N users" line reports. Deliberately independent of the page's own period and user
   * scope: it's a property of the threshold config, so narrowing the brief to one team shouldn't
   * change what the settings copy claims.
   *
   * A notch flags a severity when that severity is at or above the notch's floor (NOTCH_MIN), so
   * the same response yields all three counts. Users are counted distinctly — someone flagged at
   * two severities in the same week is still one user.
   */
  async getSensitivityCounts(): Promise<Record<string, Record<Sensitivity, number>> | undefined> {
    // .setZone before byType, same as AskAiScopeService.dateRange$ — otherwise the boundaries land
    // on UTC midnight instead of the activity page's timezone-shifted ones, and the two disagree.
    const timezone = this.store.selectSnapshot(AuthState.userTimezone) || DateTime.local().zoneName;
    const lastWeek = DateRange.byType('week', DateTime.utc().setZone(timezone).minus({ week: 1 }));
    const { from, to } = lastWeek.asApiParams();
    const fetched = await this.fetchSignalsResponse(from, to);
    if (!fetched) return undefined;
    const groups = fetched.groups;

    const counts: Record<string, Record<Sensitivity, number>> = {};
    groups.forEach(group => {
      const perNotch = {} as Record<Sensitivity, number>;
      (Object.keys(NOTCH_MIN) as Sensitivity[]).forEach(notch => {
        const flagged = new Set<string>();
        (Object.keys(group.bySeverity) as Severity[]).forEach(severity => {
          if (SEV_ORDER[severity] >= NOTCH_MIN[notch]) {
            group.bySeverity[severity]?.forEach(entry => flagged.add(entry.id));
          }
        });
        perNotch[notch] = flagged.size;
      });
      counts[group.alertId] = perNotch;
    });
    return counts;
  }

  /**
   * The userIds a scope selection includes, or undefined for "everyone" — the signals endpoint
   * takes no user parameter, so without this a filtered pick would still show unfiltered counts. A
   * group selection resolves against the managed users in the store; if those aren't loaded yet
   * this returns undefined so entries are left alone rather than emptied. Shared by
   * scopeGroupsToSelection (by-alert-type) and scopeUserGroupsToSelection (AA-214 person cards)
   * below.
   */
  private resolveAllowedUserIds(userFilter?: LazyUserSelectionSerializable): Set<string> | undefined {
    const selection = LazyUserSelection.fromSerializable(userFilter ?? {});
    const selectsEveryone = selection.type === 'users' ? selection.users.all : selection.groups.all;
    if (selectsEveryone) return undefined;

    if (selection.type === 'users') return new Set(selection.users.list);
    const managed = this.store.selectSnapshot(ManagerState.users.onReports.includeArchived(selection.includeArchivedUsers));
    if (!managed?.length) return undefined;
    return new Set(managed.filter(selection.isUserSelected).map(user => user.id));
  }

  private scopeGroupsToSelection(
    groups: AlertSignalGroup[], userFilter?: LazyUserSelectionSerializable,
  ): AlertSignalGroup[] {
    const allowed = this.resolveAllowedUserIds(userFilter);
    if (!allowed) return groups;

    // Buckets that empty out are dropped, so an alert type nobody in scope tripped stops producing
    // a card at all rather than an empty one.
    return groups.reduce<AlertSignalGroup[]>((kept, group) => {
      const bySeverity: AlertSignalGroup['bySeverity'] = {};
      (Object.keys(group.bySeverity) as Severity[]).forEach(severity => {
        const entries = group.bySeverity[severity]?.filter(entry => allowed.has(entry.id));
        if (entries?.length) bySeverity[severity] = entries;
      });
      if (Object.keys(bySeverity).length) kept.push({ alertId: group.alertId, bySeverity });
      return kept;
    }, []);
  }

  // Same "no card for someone not in scope" rule scopeGroupsToSelection applies above.
  private scopeUserGroupsToSelection(groups: UserSignalGroup[], userFilter?: LazyUserSelectionSerializable): UserSignalGroup[] {
    const allowed = this.resolveAllowedUserIds(userFilter);
    return allowed ? groups.filter(group => allowed.has(group.userId)) : groups;
  }

  // Resolves userIds to display names via ResolveService, same cache resolveFlaggedUsers uses — the
  // default 'alias' detail is enough here since person cards show no screencasts link.
  private async resolvePersonCardUserNames(userIds: string[]): Promise<Map<string, string>> {
    const names = new Map<string, string>();
    if (!userIds.length) return names;
    const users = await this.resolve.resolveUsers(userIds);
    userIds.forEach(id => names.set(id, users.get(id)?.name ?? id));
    return names;
  }

  // AlertType.severity already carries the real, currently-configured threshold/x/y per alert type
  // (see mapAlertTypeResponses) — this just re-keys it by alertId for buildFiredSignalsForUser.
  private bandConfigFromAlertTypes(alertTypes: AlertType[]): Record<string, SignalBandConfig> {
    const config: Record<string, SignalBandConfig> = {};
    alertTypes.forEach(alertType => {
      config[alertType.key] = {
        threshold: alertType.severity.threshold, x: alertType.severity.x, y: alertType.severity.y,
        direction: alertType.severity.direction,
      };
    });
    return config;
  }

  /**
   * Needs You's AA-214 combined person cards — GET activity/agent-gateway/signals?groupBy=user,
   * mapped straight to real Signal[] (see personCardToSignal) rather than PersonCard[], so every
   * downstream consumer reuses the existing by-alert-type code path unchanged.
   *
   * The backend doesn't send per-day `day` yet, so each signal's n/d/peak/peakDay is a
   * range-aggregate estimate (buildFiredSignalsForUser) until real per-day data arrives; the
   * citation link only renders once peakDay is real (FiredSignal.peakDayIsReal). Never throws
   * itself — mirrors getSignals' contract via the same thrown error.
   */
  async getPersonSignalCards(
    from: string | undefined, to: string | undefined, label: string, alertTypes: AlertType[],
    userFilter?: LazyUserSelectionSerializable, timezone?: string,
  ): Promise<Signal[]> {
    if (!from || !to) return [];
    const fetched = await this.fetchUserGroupedSignalsResponse(from, to);
    if (!fetched) throw new Error('Failed to fetch activity/agent-gateway/signals?groupBy=user');
    return this.buildPersonSignalsFromGroups(fetched, from, to, label, alertTypes, userFilter, timezone);
  }

  /** History / Dismissed's AA-214 counterpart to getDismissedSignals — same
   * includeDismissed/status-filter/scoping shape, grouped by user. Same never-throws contract. */
  async getDismissedPersonSignals(
    from: string | undefined, to: string | undefined, label: string, alertTypes: AlertType[],
    userFilter?: LazyUserSelectionSerializable, timezone?: string,
  ): Promise<Signal[] | undefined> {
    if (!from || !to) return [];
    const fetched = await this.fetchUserGroupedSignalsResponse(from, to, true);
    if (!fetched) return undefined;
    const dismissedOnly = this.filterUserGroupsByStatus(fetched, 'dismissed');
    return this.buildPersonSignalsFromGroups(dismissedOnly, from, to, label, alertTypes, userFilter, timezone);
  }

  // Shared by getPersonSignalCards/getDismissedPersonSignals — scopes, resolves names, builds each
  // user's PersonCard + chart, and maps to Signal (see getPersonSignalCards for why). `groups` is
  // assumed already fetched (and, for Dismissed, already status-filtered) by the caller.
  private async buildPersonSignalsFromGroups(
    groups: UserSignalGroup[], from: string, to: string, label: string, alertTypes: AlertType[],
    userFilter?: LazyUserSelectionSerializable, timezone?: string,
  ): Promise<Signal[]> {
    const scoped = this.scopeUserGroupsToSelection(groups, userFilter);
    if (!scoped.length) return [];

    const names = await this.resolvePersonCardUserNames(scoped.map(group => group.userId));
    const bandConfig = this.bandConfigFromAlertTypes(alertTypes);
    const dateRange = { from, to };
    // One shared weekStart for every card — each signal's peakDay resolves against the range end's
    // own week, in the caller's org timezone (not UTC). See mondayOfWeekContaining's doc comment
    // for the timezone/weekday-boundary caveats.
    const weekStart = mondayOfWeekContaining(to, timezone);

    return scoped
      .map(group => {
        const { signals, dailyPointsByType, precomputedSeverities } = buildFiredSignalsForUser(
          group.entries, bandConfig, dateRange, timezone,
        );
        // precomputedSeverities only overrides a range-aggregate-fallback type (see
        // buildFiredSignalsForUser); a type from real per-day entries computes its own severity in
        // buildPersonCard. buildPersonCardSignalLink only renders when peakDay is real
        // (FiredSignal.peakDayIsReal).
        const card = buildPersonCard(
          group.userId, names.get(group.userId) ?? group.userId, signals, weekStart, buildPersonCardSignalLink, precomputedSeverities,
        );
        if (!card) return null;
        const chart = buildPersonCardDailyChart(card.orderedTypes, signals, dailyPointsByType, weekStart, to, timezone);
        // AA-225 — the same signals/dailyPointsByType/weekStart feed the View details week matrix
        // (person-week-evidence.ts), reusing this card's order/severities so the two can't disagree.
        const weekEvidence = buildPersonWeekEvidence(
          group.userId, weekStart, card.orderedTypes, card.severities, signals, dailyPointsByType, buildPersonCardSignalLink,
          to, timezone,
        );
        return this.personCardToSignal(card, label, chart, weekEvidence);
      })
      .filter((signal): signal is Signal => signal !== null);
  }

  /**
   * Maps one AA-214 PersonCard onto the real Signal shape (see getPersonSignalCards for why).
   * Fields with no person-card equivalent reuse the same "not available yet" values as
   * buildGroupedSignal/pinAnswerToBrief (reportName/reportUrl: '', rows: []). `key` is
   * PERSON_CARD_SIGNAL_KEY, deliberately unmatched by bodyParts()'s switch, so it falls through to
   * the pre-rendered `parts` below instead of a template lookup.
   */
  private personCardToSignal(card: PersonCard, label: string, chart: PersonCardDailyColumn[], weekEvidence: PersonWeekEvidence): Signal {
    const parts = bodyToTextParts(card.body);
    const types = card.orderedTypes.map(personSignalTypeLabel);
    const type = joinWithAnd(types);
    return {
      id: card.userId, key: PERSON_CARD_SIGNAL_KEY, type, types, sev: card.badge, signalIds: card.signalIds,
      title: card.title, body: card.body.text, parts, answerParts: parts, answer: card.body.text,
      trend: chart.length > 0, chartCaption: '', bars: [], personCardChart: chart, personWeekEvidence: weekEvidence,
      personCardBodyLines: card.body.lines.map(line => lineToTextParts(line)),
      userCount: 1, userName: card.userName,
      userBars: [{ id: card.userId, name: card.userName, value: card.leadPeak }],
      threshold: card.leadThreshold, unit: card.leadUnit, direction: card.leadDirection,
      durationRatio: card.leadUnit === 'h',
      askPrompt: buildPersonCardAskPrompt(card.userName, card.orderedTypes),
      recommendation: '', draftMessage: '', followUps: [],
      reportName: '', reportUrl: '', chipContext: `${card.userName}, ${label}`,
      evidenceCount: card.signalCount, period: label, metricLabel: '', evBody: card.body.text,
      rows: [], single: true,
    };
  }

  // Keeps only entries whose status.status matches — same "drop an emptied group" rule
  // filterGroupsByStatus applies for by-alert-type, just over flat `entries` instead of buckets.
  private filterUserGroupsByStatus(groups: UserSignalGroup[], status: 'active' | 'dismissed'): UserSignalGroup[] {
    return groups.reduce<UserSignalGroup[]>((kept, group) => {
      const entries = group.entries.filter(entry => entry.status?.status === status);
      if (entries.length) kept.push({ userId: group.userId, entries });
      return kept;
    }, []);
  }

  private async fetchUserGroupedSignalsResponse(
    from: string, to: string, includeDismissed = false,
  ): Promise<UserSignalGroup[] | undefined> {
    try {
      const params: Record<string, unknown> = { from, to, groupBy: 'user', ...(includeDismissed ? { includeDismissed: true } : {}) };
      const response = await lastValueFrom(
        this.api.request<UserGroupedSignalsResponse>('get', 'activity/agent-gateway/signals', params, {}, true, '2.0'),
      );
      return parseUserGroupedSignalsResponse(response);
    } catch (error) {
      Sentry.captureException(
        error, { level: 'warning', extra: { call: 'activity/agent-gateway/signals', from, to, groupBy: 'user', includeDismissed } },
      );
      return undefined;
    }
  }

  // `from`/`to` are omitted together for the latest-signals feed (AA-327), which `page` then pages.
  private async fetchSignalsResponse(
    from: string | undefined, to: string | undefined, includeDismissed = false, page?: { limit: number, offset: number },
  ): Promise<FetchedSignals | undefined> {
    try {
      const params: Record<string, unknown> = {
        ...(from && to ? { from, to } : {}), ...(includeDismissed ? { includeDismissed: true } : {}), ...(page ?? {}),
      };
      const response = await lastValueFrom(
        this.api.request<SignalsResponse>('get', 'activity/agent-gateway/signals', params, {}, true, '2.0'),
      );
      return {
        groups: parseSignalsResponse(response),
        personWeeks: parseStoredPersonWeeks(response),
        hasPersonWeeks: Array.isArray(response?.personWeeks),
        total: typeof response?.total === 'number' ? response.total : undefined,
      };
    } catch (error) {
      // Was a silent fallback to fixture data — now logged so an outage shows up in monitoring
      // instead of masquerading as "no signals yet".
      Sentry.captureException(
        error, { level: 'warning', extra: { call: 'activity/agent-gateway/signals', from, to, includeDismissed } },
      );
      return undefined;
    }
  }

  // Resolves the live agent-gateway/alerts response's severityConfigs against the current user
  // into the shape components render. Every response contributes an AlertType, even an id with no
  // hand-authored copy — see mapAlertTypeResponses' ALERT_TYPE_COPY lookup. Only falls back to
  // mock data wholesale (FALLBACK_ALERT_TYPE_RESPONSES) if the endpoint is down/returns nothing.
  async getAlertTypes(): Promise<AlertType[]> {
    const currentUserId = this.store.selectSnapshot(AuthState.user)?.id;
    const responses = await this.fetchAlertTypeResponses();
    if (responses) this.provisionUncreatedAlerts(responses);
    return mapAlertTypeResponses(currentUserId, responses);
  }

  // Seeds the backend's default threshold/operator/exceedLimits for every alert type this company
  // (or a brand new one) has never configured — isCreated: false, no severityConfigs yet. Fired
  // and forgotten rather than awaited: the response's own default* fields already give
  // mapAlertTypeResponses enough to render correctly before any of these round-trip, and a failed
  // one just leaves that alert type "not created" to retry next time getAlertTypes runs.
  // Always created enabled: an uncreated alert reports isEnabled: false, and there is no disable
  // control yet, so passing that through left the night job never scoring the alert type.
  private provisionUncreatedAlerts(responses: AlertTypeResponse[]): void {
    responses.filter(r => !r.isCreated).forEach(r => {
      this.createCustomerAlert(r.id, true, r.defaultThreshold, r.defaultOperator, r.defaultExceedLimits).catch(() => undefined);
    });
  }

  // GET agent-gateway/alerts returns a bare array (not a { data: [...] } envelope, unlike
  // agent-gateway/alerts/customer below) — same call shape as TokenService.all otherwise. Never
  // throws — an empty/failed response just means getAlertTypes falls back to
  // FALLBACK_ALERT_TYPE_RESPONSES' mock data.
  private async fetchAlertTypeResponses(): Promise<AlertTypeResponse[] | undefined> {
    try {
      const response = await lastValueFrom(this.api.request<unknown[]>('get', 'activity/agent-gateway/alerts', {}, {}, true, '2.0'));
      return parseAlertTypeResponses(response ?? []);
    } catch (error) {
      Sentry.captureException(error, { level: 'warning', extra: { call: 'activity/agent-gateway/alerts' } });
      return undefined;
    }
  }

  // POST activity/agent-gateway/alerts/customer to create/enable an alert type for the current
  // company. Sends the alert type's own default threshold/operator/exceedLimits as both
  // severityConfig and userSeverityConfig — exceedLimits is required by the endpoint's schema
  // (400 "severityConfig must have required property 'exceedLimits'" without it). The backend
  // doesn't fill in a sensible default when threshold is omitted, it creates the alert at
  // threshold 0, which then reads as "vs threshold" == the raw value everywhere that alert type
  // shows up until someone happens to save a real threshold.
  async createCustomerAlert(
    alertTypeKey: string, enabled: boolean, threshold: number, operator: 'above' | 'below',
    exceedLimits: AlertTypeResponse['defaultExceedLimits'],
  ): Promise<void> {
    const severityConfig = { threshold, operator, exceedLimits };
    await lastValueFrom(
      this.api.request('post', 'activity/agent-gateway/alerts/customer',
        [{ alertId: alertTypeKey, enabled, severityConfig, userSeverityConfig: severityConfig }], {}, true, '2.0'),
    );
  }

  // PATCH activity/agent-gateway/alerts/customer/{alertId} to upsert the current user's
  // severityConfig entry (now including its `sensitivity` — the {name, value} pair for whichever
  // notch is selected, not just its name) plus the alert type's enabled — always the current user,
  // not the company default, since this screen edits their own thresholds. exceedLimits is left out
  // of the body: nothing in the frontend edits it yet, so there's nothing real to send for it.
  async updateCustomerAlert(
    alertTypeKey: string, enabled: boolean, sensitivity: SensitivityThreshold, threshold: number, operator: 'above' | 'below',
  ): Promise<void> {
    const userId = this.store.selectSnapshot(AuthState.user)?.id ?? null;
    const severityConfig = { userId, threshold, operator, sensitivity };
    await lastValueFrom(
      this.api.request('patch', `activity/agent-gateway/alerts/customer/${alertTypeKey}`, { enabled, severityConfig }, {}, true, '2.0'),
    );
  }

  // DELETE activity/agent-gateway/alerts/customer/{alertId} to remove the current company's
  // configuration for one alert type entirely — not the same as disabling it (PUT enabled: false),
  // it drops the record, so a later getAlertTypes would see isCreated: false again.
  async deleteCustomerAlert(alertTypeKey: string): Promise<void> {
    await lastValueFrom(
      this.api.request('delete', `activity/agent-gateway/alerts/customer/${alertTypeKey}`, {}, {}, true, '2.0'),
    );
  }

  async getPeriods(): Promise<Period[]> {
    return PERIODS;
  }

  /**
   * Returns the guidance answer for a how-to question, or undefined when the question is asking
   * for someone's data to be read. Keyword matching stands in for real retrieval — swap this for
   * the help-center search call once one exists.
   */
  guidanceFor(question: string): GuidanceAnswer | undefined {
    const text = question.toLowerCase();
    if (!GUIDANCE_OPENERS.some(opener => text.indexOf(opener) !== -1)) return undefined;
    return GUIDANCE.find(topic => topic.keywords.some(keyword => text.indexOf(keyword) !== -1)) || GUIDANCE_FALLBACK;
  }

  buildGuidanceSteps(): ConversationTurnStep[] {
    return [{ text: 'Searched the Time Doctor Help Center', tool: 'help_center_search' }];
  }

  buildSteps(signal: Signal): ConversationTurnStep[] {
    return [
      { text: `Read tracked and idle time for 12 members, ${signal.period}`, tool: 'td_get_time_summary' },
      { text: `Pulled per-day rows for the ${signal.evidenceCount} members over threshold`, tool: 'td_get_worklog' },
      { text: 'Checked what those hours were spent on', tool: 'td_get_activity_project_totals' },
    ];
  }

  /** No real model call backs these fixture turns, so there's no genuine token count to show.
   * Derives a plausible-looking one from the turn's own text (~4 chars/token, the common rough
   * English estimate) rather than a random or hardcoded number, so it varies sensibly by turn. */
  estimateUsage(question: string, answer: string): TokenUsage {
    const promptTokens = Math.max(20, Math.round(question.length / 4));
    const completionTokens = Math.max(30, Math.round(answer.length / 4));
    return { promptTokens, completionTokens, totalTokens: promptTokens + completionTokens };
  }

  /** Emits each tool-trace step index in sequence, then completes — replaces the prototype's setTimeout chain. */
  streamSteps(stepCount: number, stepDelayMs = 650): Observable<number> {
    return new Observable<number>(subscriber => {
      let i = 0;
      const interval = setInterval(() => {
        i += 1;
        subscriber.next(i);
        if (i >= stepCount) {
          clearInterval(interval);
          subscriber.complete();
        }
      }, stepDelayMs);
      return () => clearInterval(interval);
    });
  }
}
